import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { concat, fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { Section, VStack } from 'tio-ui/components/layout';
import Heading from 'tio-ui/components/heading';
import Text from 'tio-ui/components/text';
import { formatCurrency } from 'tio-ui/format';
import Chip from 'tio-ui/components/chip';
import Button from 'tio-ui/components/button';
import FilterTabs from 'tio-ui/components/filter-tabs';
import SkillChip from 'tio-ui/components/skill-chip';
import ActionableCard from 'tio-ui/components/actionable-card';
import StatCard from 'tio-ui/components/stat-card';
import { getCurrentDemoClientData } from 'tio-employee/utils/career-pathways-demo-data';
import type ReimaginedDataService from 'tio-employee/services/reimagined-data';
import {
  PhSparkle,
  PhTarget,
  PhTrendUp,
  PhCurrencyDollar,
  PhClock,
  PhBuildings,
  PhGraduationCap,
  PhCertificate,
  PhHeart,
  PhCheckCircle,
  PhArrowRight,
  PhFunnel,
  PhLightning,
} from 'ember-phosphor-icons';
import PageHero from 'tio-ui/components/page-hero';
import type {
  EmployeeCareerProfile,
  CareerPlan,
  Recommendation,
  CareerSummary,
} from 'tio-employee/services/career-pathways-api';

interface CareerPathwaysRecommendationsSignature {
  Args: {
    model: {
      recommendations: Recommendation[];
      employee: EmployeeCareerProfile;
      careerPlan: CareerPlan;
      summary: CareerSummary;
    };
  };
}

const RECOMMENDATION_CONTEXT = {
  advocate: {
    goal: 'Nurse Manager',
    skillGaps: 'Leadership, Budget, QI',
    taEligibility: '$5,250/year',
    priorities: 'Leadership, Informatics',
  },
  ups: {
    goal: 'Hub Supervisor',
    skillGaps: 'Scheduling, Coaching, Process Improvement',
    taEligibility: '$5,250/year',
    priorities: 'Safety, Leadership',
  },
  starbucks: {
    goal: 'Store Manager',
    skillGaps: 'Labor Planning, Financials, Coaching',
    taEligibility: '$5,250/year',
    priorities: 'Operations, Customer Experience',
  },
};

export default class CareerPathwaysRecommendations extends Component<CareerPathwaysRecommendationsSignature> {
  @service declare reimaginedData: ReimaginedDataService;
  @tracked activeFilter = 'all';
  @tracked savedItems: string[] = [];

  get recommendationContext() {
    if (this.reimaginedData.isDemoMode) {
      const clientId = getCurrentDemoClientData().client.id as keyof typeof RECOMMENDATION_CONTEXT;
      return RECOMMENDATION_CONTEXT[clientId] ?? RECOMMENDATION_CONTEXT.advocate;
    }

    const { employee, careerPlan, summary } = this.args.model;
    const goal = careerPlan?.targetRoleTitle || employee?.targetRole?.title || 'Your next role';
    const skillGaps = summary
      ? `${summary.totalGaps} gaps (${summary.criticalGaps} critical)`
      : 'Skill gaps pending';
    const taEligibility = employee?.taBenefit?.annualMax
      ? `${formatCurrency(employee.taBenefit.annualMax)}/year`
      : 'TA eligibility pending';
    const priorities = summary
      ? `${summary.skillsInProgress} in-progress skills`
      : 'Priorities pending';

    return { goal, skillGaps, taEligibility, priorities };
  }

  get recommendations() {
    return this.args.model.recommendations;
  }

  get filters() {
    const all = this.recommendations.length;
    const taEligible = this.recommendations.filter((rec) => rec.taEligible).length;
    const certifications = this.recommendations.filter(
      (rec) => rec.type === 'Certification'
    ).length;
    const courses = this.recommendations.filter((rec) => rec.type === 'Course').length;
    const free = this.recommendations.filter((rec) =>
      rec.cost.toLowerCase().includes('free')
    ).length;

    return [
      { id: 'all', label: 'All', count: all },
      { id: 'ta-eligible', label: 'TA Eligible', count: taEligible },
      { id: 'certifications', label: 'Certifications', count: certifications },
      { id: 'courses', label: 'Courses', count: courses },
      { id: 'free', label: 'Free/Low Cost', count: free },
    ];
  }

  setFilter = (filterId: string) => {
    this.activeFilter = filterId;
  };

  toggleSaved = (itemId: string) => {
    if (this.savedItems.includes(itemId)) {
      this.savedItems = this.savedItems.filter((id) => id !== itemId);
    } else {
      this.savedItems = [...this.savedItems, itemId];
    }
  };

  isSaved = (itemId: string) => {
    return this.savedItems.includes(itemId);
  };

  get filteredRecommendations() {
    if (this.activeFilter === 'all') return this.recommendations;
    if (this.activeFilter === 'ta-eligible')
      return this.recommendations.filter((r) => r.taEligible);
    if (this.activeFilter === 'certifications')
      return this.recommendations.filter((r) => r.type === 'Certification');
    if (this.activeFilter === 'courses')
      return this.recommendations.filter((r) => r.type === 'Course');
    if (this.activeFilter === 'free')
      return this.recommendations.filter((r) => r.cost.toLowerCase().includes('free'));
    return this.recommendations;
  }

  getBadgeIntent = (badge?: string) => {
    const intents: Record<string, 'default' | 'primary' | 'success' | 'warning' | 'danger'> = {
      'Best Match': 'success',
      'Company Priority': 'warning',
      Premium: 'primary',
      'Quick Win': 'default',
      'In Demand': 'primary',
    };
    if (!badge) return 'default';
    return intents[badge] || 'default';
  };

  <template>
    {{! template-lint-disable no-bare-strings }}
    <VStack class="data-audit w-full min-w-0">
      {{! Header }}
      <PageHero @gradient="brand">
        <:default>
          <div class="tio-row-start-between flex-col sm:flex-row gap-4">
            <div class="tio-fill-min">
              <div class="tio-row mb-2">
                <PhSparkle @size={{32}} class="shrink-0" />
                <Heading @level={{1}} class="tio-fg-inverse">Smart Recommendations</Heading>
              </div>
              <Text @size="lg" class="tio-recommendations-hero-copy">
                AI-powered learning recommendations personalized for your career goals, current
                skills, and tuition assistance benefits.
              </Text>
            </div>
            <div class="tio-recommendations-match-stat">
              <PhLightning @size={{32}} class="mx-auto mb-2" />
              <div class="font-bold">{{this.recommendations.length}} Matches</div>
              <Text @size="sm" class="tio-recommendations-match-stat__label">For You</Text>
            </div>
          </div>
        </:default>
      </PageHero>

      <div class="grid grid-cols-1 lg:grid-cols-4 gap-6 min-w-0">
        {{! Filters Sidebar }}
        <div class="space-y-4">
          <Section class="tio-bg-surface rounded-lg shadow-sm">
            <:header>
              <div class="tio-row-tight">
                <PhFunnel @size={{18}} />
                <span>Filter</span>
              </div>
            </:header>
            <:body>
              <FilterTabs
                @tabs={{this.filters}}
                @activeId={{this.activeFilter}}
                @onSelect={{this.setFilter}}
                @activeClass="tio-bg-brand-soft tio-fg-brand"
                @inactiveClass="hover:tio-bg-sunken tio-fg-secondary"
                @countActiveClass="tio-bg-brand-soft tio-fg-brand"
                @countInactiveClass="tio-bg-chip tio-fg-secondary"
                class="tio-col-tight"
              />
            </:body>
          </Section>

          {{! Why These Recommendations }}
          <Section class="tio-bg-surface rounded-lg shadow-sm">
            <:header>
              <div class="tio-row-tight">
                <PhTarget @size={{18}} class="tio-fg-brand" />
                <span>Based On</span>
              </div>
            </:header>
            <:body>
              <div class="space-y-3 text-sm">
                <div class="tio-row-top-tight">
                  <PhCheckCircle @size={{16}} class="tio-fg-success mt-0.5" />
                  <span>Your goal: <strong>{{this.recommendationContext.goal}}</strong></span>
                </div>
                <div class="tio-row-top-tight">
                  <PhCheckCircle @size={{16}} class="tio-fg-success mt-0.5" />
                  <span>Your skill gaps:
                    <strong>{{this.recommendationContext.skillGaps}}</strong></span>
                </div>
                <div class="tio-row-top-tight">
                  <PhCheckCircle @size={{16}} class="tio-fg-success mt-0.5" />
                  <span>TA eligibility:
                    <strong>{{this.recommendationContext.taEligibility}}</strong></span>
                </div>
                <div class="tio-row-top-tight">
                  <PhCheckCircle @size={{16}} class="tio-fg-success mt-0.5" />
                  <span>Company priorities:
                    <strong>{{this.recommendationContext.priorities}}</strong></span>
                </div>
              </div>
            </:body>
          </Section>

          {{! Saved Items }}
          <Section class="tio-bg-surface rounded-lg shadow-sm">
            <:header>
              <div class="tio-row-tight">
                <PhHeart @size={{18}} class="tio-fg-danger" />
                <span>Saved ({{this.savedItems.length}})</span>
              </div>
            </:header>
            <:body>
              {{#if this.savedItems.length}}
                <Text @variant="muted" @size="sm">
                  You've saved
                  {{this.savedItems.length}}
                  programs to review later.
                </Text>
                <Button @intent="primary" @appearance="outlined" class="w-full mt-3">
                  View Saved
                </Button>
              {{else}}
                <Text @variant="muted" @size="sm">
                  Click the heart icon to save programs you're interested in.
                </Text>
              {{/if}}
            </:body>
          </Section>
        </div>

        {{! Main Content }}
        <div class="lg:col-span-3 space-y-4 min-w-0">
          {{#each this.filteredRecommendations as |rec|}}
            <ActionableCard
              @title={{rec.title}}
              @matchScore={{rec.matchScore}}
              @showBanner={{rec.isTopPick}}
              @bannerText="Top Pick for You"
              @bannerClass="tio-gradient-brand tio-recommendations-banner"
              @cardClass={{if
                rec.isTopPick
                "tio-bg-surface rounded-lg border tio-border-brand ring-2 tio-ring-brand shadow-sm overflow-hidden"
                "tio-bg-surface rounded-lg border tio-border-default shadow-sm overflow-hidden"
              }}
            >
              <:badge>
                {{#if rec.badge}}
                  <Chip @intent={{this.getBadgeIntent rec.badge}}>
                    {{rec.badge}}
                  </Chip>
                {{/if}}
              </:badge>
              <:metadata>
                <div class="tio-row-flush flex-wrap gap-x-4 gap-y-1 text-sm tio-fg-muted">
                  <span class="tio-row-extra-tight">
                    <PhGraduationCap @size={{16}} class="shrink-0" />
                    <span class="truncate">{{rec.provider}}</span>
                  </span>
                  <span class="tio-row-extra-tight">
                    <PhCertificate @size={{16}} class="shrink-0" />
                    {{rec.type}}
                  </span>
                  <span class="tio-row-extra-tight">
                    <PhClock @size={{16}} class="shrink-0" />
                    {{rec.duration}}
                  </span>
                </div>
              </:metadata>

              <:skills>
                {{#each rec.skillsGained as |skill|}}
                  <SkillChip @name={{skill}} @intent="primary" @showLevel={{false}} />
                {{/each}}
              </:skills>

              <:eligibility>
                <div class="space-y-3">
                  <div class="tio-bg-brand-wash rounded-lg p-2">
                    <div class="tio-row-top-tight">
                      <PhSparkle @size={{18}} class="tio-fg-brand-accent mt-0.5" />
                      <Text @size="sm">
                        <strong>Why this?</strong>
                        {{rec.reason}}
                      </Text>
                    </div>
                  </div>

                  <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <StatCard
                      @icon={{PhCurrencyDollar}}
                      @value={{rec.cost}}
                      @label="Cost"
                      @color="green"
                      class="p-3"
                    />
                    <StatCard
                      @icon={{PhTrendUp}}
                      @value={{rec.salaryImpact}}
                      @label="Avg Salary Impact"
                      @color="green"
                      @valueClass="tio-fg-success"
                      class="p-3"
                    />
                    <StatCard
                      @icon={{PhCheckCircle}}
                      @value={{concat rec.completionRate "%"}}
                      @label="Completion Rate"
                      @color="blue"
                      class="p-3"
                    />
                    <StatCard
                      @icon={{PhBuildings}}
                      @value={{if rec.taEligible "Yes" "No"}}
                      @label="TA Eligible"
                      @color={{if rec.taEligible "green" "gray"}}
                      @valueClass={{if rec.taEligible "tio-fg-success" "tio-fg-muted"}}
                      class="p-3"
                    />
                  </div>
                </div>
              </:eligibility>

              <:actions>
                <div class="tio-flex-tight">
                  <Button
                    @intent="primary"
                    @icon={{PhArrowRight}}
                    @iconPlacement="end"
                    @label={{if rec.taEligible "Apply with TA" "Learn More"}}
                  />
                  <Button @intent="default" @appearance="outlined" @label="Add to Plan" />
                </div>
                <button
                  type="button"
                  {{on "click" (fn this.toggleSaved rec.id)}}
                  class="p-2 rounded-full hover:tio-bg-danger-soft-hover transition-colors"
                >
                  <PhHeart
                    @size={{24}}
                    class="{{if
                        (this.isSaved rec.id)
                        'tio-fg-danger tio-recommendation-saved-icon'
                        'tio-fg-disabled'
                      }}"
                    @weight={{if (this.isSaved rec.id) "fill" "regular"}}
                  />
                </button>
              </:actions>
            </ActionableCard>
          {{/each}}

          {{! Load More }}
          <div class="text-center py-6">
            <Button @intent="default" @appearance="outlined">
              Load More Recommendations
            </Button>
            <Text @variant="muted" @size="sm" class="mt-2">
              Showing 5 of 23 recommendations
            </Text>
          </div>
        </div>
      </div>
    </VStack>
  </template>
}
